import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy, faClock, faCheckCircle, faLink, faPlus, faUser, faEnvelope,
  faPhone, faHashtag, faUniversity, faXmark, faQrcode,
} from '@fortawesome/free-solid-svg-icons';
import { bankList } from '../utils/bankList';
import { fmtMoney } from '../utils/formatMoney';
import { UiPageHeader, UiButton, UiInput, UiSelect } from '../components/ui';
import { CountdownTimer } from '../components/shared';

const EMPTY_FORM = {
  amount: '',
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  reference_id: '',
  bank_name: '',
  bank_number: '',
};

/** qr = QR พร้อมเพย์ (สุ่มเศษสตางค์) · slip = โอนยอดเต็มแล้วแนบสลิป (ไม่สุ่มสตางค์) */
type OrderMode = 'qr' | 'slip';

const ORDER_ENDPOINT: Record<OrderMode, string> = {
  qr: '/merchants/orders',
  slip: '/merchants/orders/slip',
};

const CreateOrderPage: React.FC = () => {
  const { t } = useTranslation();
  // ค่าเริ่มต้น = โอน + แนบสลิป (ไม่สุ่มเศษสตางค์)
  const [mode, setMode] = useState<OrderMode>('slip');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const isSlip = !!orderResult?.slip_only;

  // response ฝั่ง portal ใช้ banks{} แต่เผื่อทรง bank_info ของ External/P2P หลุดมา
  const resultBank = useMemo(() => {
    const r: any = orderResult;
    if (!r) return null;
    if (r.banks) return r.banks;
    if (r.bank_info) {
      return {
        name: r.bank_info.bank_name ?? r.bank_info.name,
        account_number: r.bank_info.account_number,
        bank_code: r.bank_info.bank_code,
        account_name: r.bank_info.account_name,
      };
    }
    return null;
  }, [orderResult]);

  const copyToClipboard = async (text: string, message = t('common.copiedUrl')) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(message);
        return;
      }
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        if (document.execCommand('copy')) toast.success(message);
        else throw new Error('Copy command failed');
      } catch {
        toast.error(t('common.cannotCopyManual'));
      } finally {
        document.body.removeChild(textArea);
      }
    } catch {
      toast.error(t('common.cannotCopyManual'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.bank_name) { toast.error(t('order.selectBankRequired')); return; }
    if (!formData.bank_number) { toast.error(t('order.accountRequired')); return; }

    setLoading(true);
    try {
      const response = await merchantApi.post(ORDER_ENDPOINT[mode], {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      setOrderResult(response.data.data);
      toast.success(t('order.createSuccess'));
      setFormData(EMPTY_FORM);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  // Poll order status while a result is shown; clear panel shortly after payment
  useEffect(() => {
    if (!orderResult?.order_number) return;
    const orderNumber = orderResult.order_number;
    let failures = 0;
    const interval = setInterval(async () => {
      try {
        const res = await merchantApi.get(`/merchants/orders/status/${orderNumber}`);
        failures = 0;
        const status = res.data.data.status;
        if (status === 'paid') {
          clearInterval(interval);
          toast.success(t('order.paymentSuccess'));
          setTimeout(() => setOrderResult(null), 3000);
        } else if (status === 'expired' || status === 'cancelled') {
          // slip order มี TTL 30 นาที — ไม่ต้อง poll ต่อเมื่อจบรอบแล้ว
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Error checking status:', error);
        if (++failures >= 5) clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [orderResult?.order_number, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faPlus}
        title={t('order.createNew')}
        subtitle={t('order.createDesc')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Form ─────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
          {/* ── Mode tabs: QR พร้อมเพย์ / โอน + แนบสลิป ─────────── */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700 -mx-6 px-6 mb-4">
            {([
              { key: 'qr' as const, icon: faQrcode, label: t('order.modeQr') },
              { key: 'slip' as const, icon: faUniversity, label: t('order.modeSlip') },
            ]).map(item => {
              const active = mode === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setMode(item.key)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    active
                      ? ''
                      : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                  }`}
                  style={active ? { borderColor: 'var(--theme-primary)', color: 'var(--theme-primary)' } : undefined}
                >
                  <FontAwesomeIcon icon={item.icon} />
                  {item.label}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 dark:text-slate-400 mb-5">
            {mode === 'qr' ? t('order.modeQrHint') : t('order.modeSlipHint')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <UiInput
              label={t('order.amountBaht')}
              required
              prefix="฿"
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="1"
              placeholder="0.00"
              className="text-lg font-semibold"
            />

            <UiInput
              label={t('order.customerName')}
              icon={faUser}
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              placeholder={t('order.customerNamePlaceholder')}
            />

            <UiInput
              label={t('order.customerEmail')}
              icon={faEnvelope}
              type="email"
              name="customer_email"
              value={formData.customer_email}
              onChange={handleChange}
              placeholder="email@example.com"
            />

            <UiInput
              label={t('order.customerPhone')}
              icon={faPhone}
              type="tel"
              name="customer_phone"
              value={formData.customer_phone}
              onChange={handleChange}
              placeholder="0812345678"
            />

            <UiInput
              label={t('order.referenceId')}
              icon={faHashtag}
              required
              type="text"
              name="reference_id"
              value={formData.reference_id}
              onChange={handleChange}
              placeholder={t('order.referenceIdPlaceholder')}
              hint={t('order.referenceIdUnique')}
            />

            <div className="grid grid-cols-2 gap-4">
              <UiSelect
                label={t('common.bank')}
                icon={faUniversity}
                required
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
              >
                <option value="">{t('common.selectBank')}</option>
                {bankList.filter(bank => bank.isActive).map(bank => (
                  <option key={bank.bankCode} value={bank.shortName}>
                    {bank.shortName} - {bank.bankName}
                  </option>
                ))}
              </UiSelect>

              <UiInput
                label={t('order.accountNumber')}
                required
                type="text"
                name="bank_number"
                value={formData.bank_number}
                onChange={handleChange}
                placeholder="1234567890"
              />
            </div>

            <UiButton
              type="submit"
              loading={loading}
              icon={mode === 'qr' ? faQrcode : faUniversity}
              className="w-full justify-center"
            >
              {loading
                ? t('common.creating')
                : (mode === 'qr' ? t('order.createWithQr') : t('order.createWithSlip'))}
            </UiButton>
          </form>
        </div>

        {/* ── Order Result ─────────────────────────────────────── */}
        {orderResult ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">
            {/* Result Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} />
                {t('order.createSuccess')}
              </h2>
              <button
                onClick={() => setOrderResult(null)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('order.orderNumber')}</div>
                  <div className="font-bold text-gray-800 dark:text-slate-100">{orderResult.order_number}</div>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('order.statusLabel')}</div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full text-xs font-semibold">
                    <FontAwesomeIcon icon={faClock} />
                    {orderResult.status}
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                  <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('order.fullAmount')}</div>
                  <div className="font-bold text-gray-800 dark:text-slate-100">฿{fmtMoney(orderResult.base_amount || orderResult.amount || 0)}</div>
                </div>
                <div
                  className="rounded-xl p-4 border-2"
                  style={{ background: 'var(--theme-primary-light)', borderColor: 'var(--theme-primary)' }}
                >
                  <div className="text-xs mb-1" style={{ color: 'var(--theme-primary)' }}>{t('order.amountToPay')}</div>
                  <div className="font-bold text-2xl" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(orderResult.total_amount || 0)}</div>
                </div>
              </div>

              {/* Payment URL */}
              {orderResult.payment_url && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-5 border-2 border-orange-300 dark:border-orange-800">
                  <h3 className="text-orange-700 dark:text-orange-300 font-semibold mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faLink} />
                    {t('order.paymentUrlTitle')}
                  </h3>
                  <div className="flex gap-2 items-center bg-white dark:bg-slate-800 rounded-lg p-3">
                    <input
                      type="text"
                      value={orderResult.payment_url}
                      readOnly
                      className="flex-1 bg-transparent border-none text-sm text-gray-700 dark:text-slate-200 focus:outline-none"
                    />
                    <button
                      onClick={() => copyToClipboard(orderResult.payment_url)}
                      className="px-4 py-2 rounded-lg text-white font-medium text-sm flex items-center gap-2 transition-all hover:-translate-y-0.5"
                      style={{ background: 'var(--theme-primary-gradient)' }}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy
                    </button>
                  </div>
                  <p className="text-orange-600 dark:text-orange-400 text-xs mt-2">{t('order.sendUrlToCustomer')}</p>
                </div>
              )}

              {/* Payment Info */}
              <div className="text-center">
                {orderResult.qr_code_url || orderResult.qr_code ? (
                  <>
                    <h3 className="text-gray-700 dark:text-slate-200 font-semibold mb-4 flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faQrcode} style={{ color: 'var(--theme-primary)' }} />
                      {t('order.qrCodeTitle')}
                    </h3>
                    <img
                      src={orderResult.qr_code_url || orderResult.qr_code}
                      alt="QR Code"
                      className="mx-auto w-48 h-48 rounded-xl border-4 border-gray-200 dark:border-slate-700 shadow-lg bg-white"
                    />
                    <p className="text-gray-500 dark:text-slate-400 mt-3 text-sm">
                      {t('order.scanQrToPay')} <strong style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(orderResult.total_amount || 0)}</strong>
                    </p>
                  </>
                ) : resultBank ? (
                  <>
                    <h3 className="text-gray-700 dark:text-slate-200 font-semibold mb-4 flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faUniversity} style={{ color: 'var(--theme-primary)' }} />
                      {t('order.bankTransferInfo')}
                    </h3>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-left space-y-2 border border-blue-200 dark:border-blue-800">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-slate-400 text-sm">{t('common.bank')}</span>
                        <span className="font-bold text-gray-800 dark:text-slate-100">{resultBank.name}</span>
                      </div>
                      {resultBank.account_name && (
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-slate-400 text-sm">{t('common.accountName')}</span>
                          <span className="font-bold text-gray-800 dark:text-slate-100">{resultBank.account_name}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 dark:text-slate-400 text-sm">{t('order.accountNumber')}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-gray-800 dark:text-slate-100">{resultBank.account_number}</span>
                          <button
                            onClick={() => copyToClipboard(resultBank.account_number, t('common.copiedAccount'))}
                            className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-xs px-2 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </div>
                      <div
                        className="rounded-lg p-3 mt-2 border-2"
                        style={{ background: 'var(--theme-primary-light)', borderColor: 'var(--theme-primary)' }}
                      >
                        <span className="text-xs font-medium block text-center mb-1" style={{ color: 'var(--theme-primary)' }}>{t('order.amountToTransfer')}</span>
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-bold text-xl" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(orderResult.total_amount || 0)}</span>
                          <button
                            onClick={() => copyToClipboard((orderResult.total_amount || 0).toFixed(2), t('common.copiedAmount'))}
                            className="text-xs px-2 py-1 rounded-lg bg-white/60 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                            style={{ color: 'var(--theme-primary)' }}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 mt-3">
                      <p className="text-red-600 dark:text-red-400 text-xs text-center font-semibold">
                        {/* slip = ยอดเต็มไม่มีเศษสตางค์ · QR ที่ปิด response_qr_enabled ก็ตกมา branch นี้และยังมีเศษสตางค์จริง */}
                        {isSlip ? t('order.transferExactNoSatang') : t('order.transferExact')}
                      </p>
                    </div>
                    {isSlip && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 mt-2">
                        <p className="text-blue-600 dark:text-blue-400 text-xs text-center">
                          <FontAwesomeIcon icon={faLink} className="mr-1" />
                          {t('order.slipUploadHint')}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-red-500 dark:text-red-400">{t('order.cannotCreatePayment')}</div>
                )}
              </div>

              {/* Countdown */}
              {orderResult.expires_at && (
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl px-5 py-4 text-center">
                  <p className="text-white/80 text-sm mb-1">
                    <FontAwesomeIcon icon={faClock} className="mr-2" />
                    {t('order.expiresIn')}
                  </p>
                  <div className="text-3xl font-bold tabular-nums">
                    <CountdownTimer expiresAt={orderResult.expires_at} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ── Empty State ────────────────────────────────────── */
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-12 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-slate-700/50 flex items-center justify-center mb-6">
              <FontAwesomeIcon icon={mode === 'qr' ? faQrcode : faUniversity} className="text-4xl text-gray-400 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-slate-200 mb-2">{t('order.noOrder')}</h3>
            <p className="text-gray-500 dark:text-slate-400 text-sm max-w-xs">
              {mode === 'qr' ? t('order.noOrderDesc') : t('order.noOrderDescSlip')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateOrderPage;
